import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";

import { useIntl } from "react-intl";

import { HeaderButton, HeaderButtonLoading } from "../../components";
import { TeamIdentifiersComponent } from "../TeamIdentifiers/TeamIdentifiersComponent";

import { useTheme } from "arkh";

import { onlyUnique } from "../../utils";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";

import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { useNewFetchKeyOnNav } from "../../useNewFetchKeyOnNav";
import { TeamIcon } from "../TeamIdentifiers/teamIcons";
import { useAlert } from "../../common/Alert";

import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { TeamAdminIdentifiersScreenQuery } from "./__generated__/TeamAdminIdentifiersScreenQuery.graphql";
import { TeamAdminIdentifiersScreen_team$key } from "./__generated__/TeamAdminIdentifiersScreen_team.graphql";
import { TeamAdminIdentifiersScreenMutation } from "./__generated__/TeamAdminIdentifiersScreenMutation.graphql";

type Navigation = NativeStackNavigationProp<any>;

const TeamEditIdentifiers = (props: {
  navigation: Navigation;
  teamId: string;
  fetchKey: string;
}) => {
  const data = useLazyLoadQuery<TeamAdminIdentifiersScreenQuery>(
    graphql`
      query TeamAdminIdentifiersScreenQuery($teamId: ID!) {
        store {
          id
          team(id: $teamId) {
            id
            name
            icon
            color
            ...TeamAdminIdentifiersScreen_team
          }
          publicTeams {
            id
            name
            icon
            color
          }
        }
      }
    `,
    { teamId: props.teamId },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const teamId = props.teamId;

  const usedColors = useMemo(
    () =>
      data?.store.publicTeams
        .filter((team) => team.id !== teamId)
        .flatMap(({ color }) => (color ? [color] : []) as string[])
        .filter(onlyUnique) ?? [],
    [data?.store.publicTeams, teamId]
  );
  const usedIcons = useMemo(
    () =>
      data?.store.publicTeams
        .filter((team) => team.id !== teamId)
        .flatMap(({ icon }) => (icon ? [icon] : []) as TeamIcon[])
        .filter(onlyUnique) ?? [],
    [data?.store.publicTeams, teamId]
  );

  if (!data.store.team) {
    return null;
  }
  return (
    <TeamEditIdentifiersComponent
      key={props.fetchKey}
      team={data.store.team}
      usedColors={usedColors}
      usedIcons={usedIcons}
      navigation={props.navigation}
    />
  );
};

function TeamEditIdentifiersComponent(props: {
  team: TeamAdminIdentifiersScreen_team$key;
  usedColors: string[];
  usedIcons: TeamIcon[];
  navigation: Navigation;
}) {
  const team = useFragment(
    graphql`
      fragment TeamAdminIdentifiersScreen_team on Team {
        id
        color
        icon
        name
        isPrivate
      }
    `,
    props.team
  );

  const theme = useTheme();
  const [color, setSelectedColor] = useState(team.color ?? theme.primary);
  const [icon, setSelectedIcon] = useState(team.icon ?? "default");
  const [name, setName] = useState(team.name);

  const onDone = useCallback(() => {
    props.navigation.goBack();
  }, [props.navigation]);
  const intl = useIntl();
  const [save, loading] = useMutation<TeamAdminIdentifiersScreenMutation>(
    graphql`
      mutation TeamAdminIdentifiersScreenMutation(
        $teamId: ID!
        $newName: String!
        $newIcon: TeamIcon!
        $newColor: String!
      ) {
        team(id: $teamId) {
          updateName(newName: $newName) {
            id
            name
          }
        }
        team(id: $teamId) {
          updateIcon(newIcon: $newIcon) {
            id
            icon
          }
        }
        team(id: $teamId) {
          updateColor(newColor: $newColor) {
            id
            color
          }
        }
      }
    `
  );

  const Alert = useAlert();

  const onSave = useCallback(() => {
    save({
      variables: {
        teamId: team.id,
        newName: name,
        newIcon: icon,
        newColor: color,
      },
      onCompleted: () => {
        onDone();
      },
      onError: () => {
        Alert.alert(
          intl.formatMessage({
            defaultMessage: "Failed to update name",
            description: "Failed to update name alert: title",
          }),
          intl.formatMessage({
            defaultMessage: "The team name could not be updated at the moment",
            description: "Failed to update name alert: description",
          })
        );
      },
    });
  }, [Alert, color, icon, intl, name, onDone, save, team.id]);

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerLeft: () => <HeaderButton type="CANCEL" onPress={onDone} />,
      title: intl.formatMessage({
        defaultMessage: "Team Name",
        description: "TeamAdminEditTeamNameScreen header title",
      }),
      headerTitleAlign: "center",
      headerRight: () => (
        <>
          {loading ? (
            <HeaderButtonLoading />
          ) : (
            <HeaderButton type="SAVE" onPress={() => onSave()} />
          )}
        </>
      ),
    });
  }, [intl, loading, onDone, onSave, props.navigation, save]);

  return (
    <TeamIdentifiersComponent
      usedColors={props.usedColors}
      usedIcons={props.usedIcons}
      color={color}
      setColor={setSelectedColor}
      icon={icon ?? "default"}
      name={name}
      setName={setName}
      setIcon={setSelectedIcon}
      mode="edit"
      LockOption={null}
      isPrivate={team.isPrivate}
    />
  );
}

export function TeamEditIdentifiersScreenComponent(props: {
  teamId: string;
  navigation: Navigation;
}) {
  const navFk = useNewFetchKeyOnNav();
  return (
    <LazyQueryNetworkBoundrary fetchKey={navFk}>
      {useCallback(
        (fetchKey) => (
          <TeamEditIdentifiers
            navigation={props.navigation}
            teamId={props.teamId}
            fetchKey={fetchKey}
          />
        ),
        [props.navigation, props.teamId]
      )}
    </LazyQueryNetworkBoundrary>
  );
}
